import { Observable, catchError, of, shareReplay, tap } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import {
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import {
  COUNTRY_FLAG_BY_CURRENCY,
  CURRENCY,
  OUT_OF_STANDARD_CURRENCY,
} from '@cxc/core/enums';

import { CurrencyEmoji } from '@cxc/core/models';
import { FxDisclosureWire } from '@models/API/fx-disclosure.model';

import { CurrenciesApi } from '@services/API/trusts/currency/currency.service';

import { knownCurrencyOr } from '@utils/format-money';

// Overview's stored figures are MXN (LEN-1808). Facility Insights converts
// from the operation's own currency, so probes on that page use that source
// instead of this default.
const BASE_CURRENCY = OUT_OF_STANDARD_CURRENCY.MXN;
const PROBE_TTL_MS = 60_000;
const GENERIC_UNAVAILABLE = 'Exchange rate unavailable';

@Injectable({ providedIn: 'root' })
export class CurrencyState {
  private currenciesApi = inject(CurrenciesApi);

  private currencys = signal<OUT_OF_STANDARD_CURRENCY[]>([]);
  // Populated asynchronously, after `currencys` - a currency starts enabled
  // and flips to disabled only once its probe answers "no rate" (Escenario
  // 6). Never blocks rendering the selector on the probes finishing.
  private unavailableReasons = signal<Map<OUT_OF_STANDARD_CURRENCY, string>>(
    new Map()
  );

  // One in-flight/replayed GET per pair so Overview + Insights/Concentrations
  // don't each fire 3 silent rate checks on every operation change.
  private probes = new Map<string, { at: number; $: Observable<unknown> }>();

  public flagsAndCurrency = computed(() => {
    const unavailableReasons = this.unavailableReasons();
    return this.currencys().map<CurrencyEmoji>(value => ({
      currency: value,
      emojiFlag: COUNTRY_FLAG_BY_CURRENCY[value],
      // `undefined`, not `false`, when available - callers/tests that never
      // heard of Escenario 6 keep matching a plain `{currency, emojiFlag}`.
      disabled: unavailableReasons.has(value) ? true : undefined,
      disabledReason: unavailableReasons.get(value),
    }));
  });
  // LEN-1808 Escenario 4: persists across Overview <-> facility navigation
  // because this service is `providedIn: 'root'` and lives for the SPA's
  // lifetime -- not because it's written anywhere durable. A hard reload or
  // a deep link straight into a facility starts a new instance with no
  // selection, same as before this scenario existed (review S4).
  public currencySelected = signal<undefined | CurrencyEmoji>(undefined);

  public currencySelected$ = toObservable(this.currencySelected);

  /**
   * The currency the portfolio screens format their amounts in (LEN-2464):
   * the selected one, or MXN -- what Overview's stored figures are
   * (LEN-1808) -- until the catalogue has seeded a selection. One copy here
   * instead of the `?? CURRENCY.MXN` each screen used to repeat (MR !1265
   * review, S2).
   *
   * Not for a figure the API always returns in the facility's own currency
   * (e.g. Overview's Maximum credit amount): that one is native regardless
   * of the selector.
   *
   * Narrowed, not cast (MR !1268 review): OUT_OF_STANDARD_CURRENCY carries
   * codes the formatters don't know (EU, L), and those fall back to MXN
   * like an empty selection instead of reaching `decodeChart` unknown.
   */
  public displayCurrency = computed(() =>
    knownCurrencyOr(this.currencySelected()?.currency, CURRENCY.MXN)
  );

  /**
   * The operation's own currency (LEN-1814 Escenario 4), set only by
   * `setCurrencyByApiAndSetSelected` -- Overview has no single operation to
   * be native to, so this stays `undefined` on that page (`setCurrencysByApi`
   * clears it) and the selector shows no Native/Converted badge there.
   */
  private _nativeCurrency = signal<OUT_OF_STANDARD_CURRENCY | undefined>(
    undefined
  );

  public nativeCurrency = this._nativeCurrency.asReadonly();

  /**
   * Whether the currency on screen is the one the selected facility already
   * reports in, vs. a re-expressed (converted) reading -- the distinction
   * LEN-1814's Escenario 4 badge exists to state, mirroring financials-mfe's
   * Reporting currency badge (LEN-1812).
   */
  public isNative = computed(
    () =>
      !!this.currencySelected() &&
      this.currencySelected()?.currency === this._nativeCurrency()
  );

  constructor() {
    // The selector writes `currencySelected` from its own `onCurrencyPicked`
    // (currency.component.ts), so no method of this class sees the user switch
    // currency. Whatever the write path, a new selection means the charts on
    // screen are about to be refetched in the new currency — and with "first
    // closing wins" below, a leftover factor would beat the one those charts
    // are about to report (MR !1203 B1).
    effect(() => {
      this.currencySelected();
      untracked(() => this.clearAppliedRate());
    });
  }

  public setDefaultCurrency() {
    this.currencySelected.set(this.flagsAndCurrency()[0]);
  }

  /**
   * The rate the charts on the current page were converted with (LEN-2090).
   *
   * Kept here rather than passed down from each page on purpose. The disclosure has to
   * read the same in all four sections - that is scenario 1 of LEN-1809, and LEN-2089
   * had to remove three literals that had already drifted apart precisely because each
   * page owned its own copy. Making every page remember to hand the rate to the
   * selector would rebuild that, one forgotten binding at a time. A page publishes what
   * it saw; the selector reads it wherever it is mounted.
   *
   * Read-only outside: `recordAppliedRate` is the only way in, so the
   * closing-only rule below cannot be sidestepped with a direct `set`.
   */
  private _appliedRate = signal<FxDisclosureWire | undefined>(undefined);

  public appliedRate = this._appliedRate.asReadonly();

  /**
   * Records the rate a chart reported, keeping the **closing** one.
   *
   * A page shows both kinds at once - Assets, Borrowing Base and Core Metrics convert
   * at a closing rate, Collection at the average of each month - and the tooltip
   * has room for one. Closing wins because that is the rate the copy describes, and
   * because an average is one number per month: naming a single one would be picking a
   * month without saying which.
   *
   * First one wins among closings. After LEN-2126 they will agree, since they will all
   * resolve at the same cut-off; today they can differ, and taking the last would make
   * the tooltip depend on the order the charts happened to arrive.
   */
  public recordAppliedRate(fx: FxDisclosureWire | undefined) {
    if (!fx || fx.basis !== 'closing') {
      return;
    }

    if (this._appliedRate()?.basis === 'closing') {
      return;
    }

    this._appliedRate.set(fx);
  }

  /**
   * Dropped whenever the catalogue is reloaded or the currency changes, because the
   * charts are about to be refetched and the old factor would sit in the tooltip
   * describing figures that are no longer on screen.
   */
  public clearAppliedRate() {
    this._appliedRate.set(undefined);
  }

  setCurrencysByApi() {
    this.clearAppliedRate();
    // Overview has no single operation to be native to (LEN-1814 Escenario
    // 4 is facility-scoped) -- clear a native currency left over from a
    // previous facility visit so the badge doesn't survive the navigation.
    this._nativeCurrency.set(undefined);
    // LEN-1808 Escenario 4: if the user already picked a currency this
    // session (Overview or a facility visited earlier), keep it across a
    // remount of Overview instead of snapping back to the default. Only
    // seed a default when nothing has been chosen yet. The lender dropdown
    // (staff-select-lender) still forces a fresh default on its own, via
    // an explicit `setDefaultCurrency()` call on lender change.
    return this.currenciesApi.getCurrencys().pipe(
      tap(res => {
        this.currencys.set(res);
        const selected = this.currencySelected()?.currency;
        // Same class as review B2 on the facility path: a selection
        // carried into an Overview remount is not guaranteed to still
        // be in this catalogue. Fall back to the default if it isn't.
        if (!selected || !res.includes(selected)) {
          this.setDefaultCurrency();
        }
        this.probeAvailability(res);
      })
    );
  }

  // this was done in a hurry it must be redone to be more code frendly
  setCurrencyByApiAndSetSelected(
    currencyCode: string,
    loaderKey: boolean | string = true
  ) {
    this.clearAppliedRate();
    // LEN-1808 Escenario 4: `currencyCode` is the operation's own currency --
    // still the right default the first time a facility loads this session
    // (e.g. a deep link straight into a facility, nothing chosen yet), and
    // still the right FX probe source below regardless of what's on screen.
    // But it must NOT override a currency the user already selected earlier
    // this session (Overview, or a previous facility) -- that's the
    // persistence this scenario is about.
    const preferredCurrency = this.currencySelected()?.currency ?? currencyCode;

    // Deliberately NOT cleared while the GET is in flight (MR !1211 S1): the
    // catalogue is global and cached, so emptying it bought nothing and made
    // the selector — which renders only when there are options (LEN-2125) —
    // unmount and remount on every facility navigation, on all four pages.
    // The tap below replaces it wholesale when the response lands.
    return this.currenciesApi.getCurrencys(loaderKey).pipe(
      tap(res => {
        this.currencys.set(res);
        // review B2: `preferredCurrency` can be a selection carried over
        // from a previous page -- nothing guarantees it's still in *this*
        // catalogue. `currencyCode` (the operation's own currency) is the
        // fallback, same as before this scenario existed.
        this.setCurrency(preferredCurrency, currencyCode);
        // MR !1246 review, blocker 1: `res.currency` is a free-form string
        // from the API, not a validated enum member. `sourceFrom` below
        // falls back to MXN for anything it doesn't recognize, which is
        // harmless for a probe direction but would make the badge assert
        // "Native" on a facility that doesn't actually report in MXN.
        // Without a currency we can vouch for, show no badge -- same as
        // Overview. Set in the same tick as `currencySelected` above (review
        // suggestion 3) so the badge can't flash "Converted" before this
        // GET resolves.
        this._nativeCurrency.set(
          this.isKnownCurrency(currencyCode) ? currencyCode : undefined
        );
        this.probeAvailability(res, this.sourceFrom(currencyCode));
      })
    );
  }

  /**
   * Fires one rate check per currency that is not already the conversion
   * source. Each pair is memoized for `PROBE_TTL_MS` so a second page that
   * loads the same catalogue does not hit `/rate` again.
   */
  private probeAvailability(
    currencies: OUT_OF_STANDARD_CURRENCY[],
    source: OUT_OF_STANDARD_CURRENCY = BASE_CURRENCY
  ): void {
    for (const currency of currencies) {
      if (currency === source) {
        continue;
      }
      this.ensureProbe(source, currency);
    }
  }

  private ensureProbe(
    source: OUT_OF_STANDARD_CURRENCY,
    target: OUT_OF_STANDARD_CURRENCY
  ): void {
    const key = `${source}->${target}`;
    const cached = this.probes.get(key);
    if (cached && Date.now() - cached.at < PROBE_TTL_MS) {
      cached.$.subscribe();
      return;
    }
    const probe$ = this.currenciesApi.getCurrencyRate(source, target).pipe(
      tap(() => this.markAvailable(target)),
      catchError((error: HttpErrorResponse) => {
        this.probes.delete(key);
        // review B1: `source` is passed through as the fallback -- it's
        // never itself probed (see the `continue` above), so it's always
        // safe to land on.
        this.markUnavailable(target, this.reasonFrom(error), source);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.probes.set(key, { at: Date.now(), $: probe$ });
    probe$.subscribe();
  }

  private sourceFrom(currencyCode: string): OUT_OF_STANDARD_CURRENCY {
    return this.isKnownCurrency(currencyCode) ? currencyCode : BASE_CURRENCY;
  }

  private isKnownCurrency(
    currencyCode: string
  ): currencyCode is OUT_OF_STANDARD_CURRENCY {
    return (Object.values(OUT_OF_STANDARD_CURRENCY) as string[]).includes(
      currencyCode
    );
  }

  private reasonFrom(error: HttpErrorResponse): string {
    const detail = error.error?.detail;
    if (error.status === 400 && typeof detail === 'string') {
      return detail;
    }
    return GENERIC_UNAVAILABLE;
  }

  private markAvailable(currency: OUT_OF_STANDARD_CURRENCY): void {
    if (!this.unavailableReasons().has(currency)) {
      return;
    }
    const next = new Map(this.unavailableReasons());
    next.delete(currency);
    this.unavailableReasons.set(next);
  }

  private markUnavailable(
    currency: OUT_OF_STANDARD_CURRENCY,
    reason: string,
    fallback: OUT_OF_STANDARD_CURRENCY
  ): void {
    const next = new Map(this.unavailableReasons());
    next.set(currency, reason);
    this.unavailableReasons.set(next);

    // review B1: a currency this same signal update just marked `disabled`
    // (above) cannot also stay `currencySelected` -- that's the exact state
    // Escenario 6 exists to prevent (a selected-but-unconvertible option),
    // and it became reachable once Escenario 4 stopped forcing every
    // facility load back to the operation's own (always-resolvable)
    // currency. `fallback` is this probe's `source`, which is never itself
    // probed, so it's always safe to land on.
    if (this.currencySelected()?.currency === currency) {
      this.setCurrency(fallback);
    }
  }

  /**
   * @param fallbackCode Used, with a warning, if `currencyCode` isn't in the
   *   current catalogue (review B2) -- e.g. a currency preserved from a
   *   previous page (Escenario 4) that this one's `getCurrencys()` didn't
   *   return. Omit it for a caller with nothing sensible to fall back to
   *   (e.g. the user picking straight off the rendered `p-select` options,
   *   which can't name a currency outside the catalogue in the first place).
   */
  setCurrency(currencyCode: string, fallbackCode?: string): void {
    const available = this.flagsAndCurrency();
    const found = available.find(item => item.currency === currencyCode);
    if (found) {
      this.currencySelected.set(found);
      return;
    }

    if (fallbackCode !== undefined && fallbackCode !== currencyCode) {
      console.warn(
        `Currency ${currencyCode} not available, falling back to ${fallbackCode}`
      );
      this.setCurrency(fallbackCode);
      return;
    }

    console.warn(`Currency ${currencyCode} not available`);
  }
}
